import type { KidSkillId, KidSkillPublic } from './kidSkill.js';

export const KID_SKILLS_CATALOG: Record<KidSkillId, KidSkillPublic> = {
  storyteller: {
    id: 'storyteller',
    title: 'Storyteller',
    description: 'Stories, characters, and adventures.',
    iconEmoji: '📖',
    accentColor: '#7c3aed',
    starters: [
      'Let’s start a story about a brave kid and a mystery door.',
      'Help me invent a silly superhero.',
      'Continue our story with a surprise twist.',
    ],
  },
  mathematician: {
    id: 'mathematician',
    title: 'Mathematician',
    description: 'Math with steps—not just answers.',
    iconEmoji: '➗',
    accentColor: '#2563eb',
    starters: [
      'Help me understand fractions with a pizza example.',
      'Walk me through this problem step by step:',
      'Give me a quick practice quiz on multiplication.',
    ],
  },
  'geography-explorer': {
    id: 'geography-explorer',
    title: 'Geography Explorer',
    description: 'Countries, cultures, and maps.',
    iconEmoji: '🌍',
    accentColor: '#059669',
    starters: [
      'Pick a country and tell me three cool facts.',
      'Quiz me on world capitals—start easy!',
      'Where should we explore today?',
    ],
  },
  scientist: {
    id: 'scientist',
    title: 'Scientist',
    description: 'Ask why, test ideas, learn how things work.',
    iconEmoji: '🔬',
    accentColor: '#0d9488',
    starters: [
      'Why is the sky blue? Explain like I’m curious, not bored.',
      'Help me design a simple experiment I can try at home.',
      'What’s a cool science fact about animals?',
    ],
  },
  'word-wizard': {
    id: 'word-wizard',
    title: 'Word Wizard',
    description: 'Vocabulary, reading, and writing fun.',
    iconEmoji: '✨',
    accentColor: '#c026d3',
    starters: [
      'Give me a new word and use it in a silly sentence.',
      'Help me write a short poem about something I pick.',
      'What’s a synonym for “happy” that sounds fancy?',
    ],
  },
  artist: {
    id: 'artist',
    title: 'Artist',
    description: 'Draw, design, and imagine visually.',
    iconEmoji: '🎨',
    accentColor: '#ea580c',
    starters: [
      'Describe a picture I could draw step by step.',
      'Help me invent a character’s outfit and colors.',
      'Give me a creative art challenge for today.',
    ],
  },
  coder: {
    id: 'coder',
    title: 'Young Coder',
    description: 'Logic, patterns, and how computers think.',
    iconEmoji: '💻',
    accentColor: '#4f46e5',
    starters: [
      'Explain loops with a real-life example.',
      'Help me plan a tiny game idea without writing code yet.',
      'What’s an algorithm? Give me a kid-friendly example.',
    ],
  },
  historian: {
    id: 'historian',
    title: 'History Detective',
    description: 'Past people, places, and big moments.',
    iconEmoji: '🏛️',
    accentColor: '#a16207',
    starters: [
      'Tell me a surprising fact about ancient Egypt.',
      'Who was an important inventor and what did they do?',
      'Compare life for kids 100 years ago vs today.',
    ],
  },
  musician: {
    id: 'musician',
    title: 'Music Maker',
    description: 'Rhythm, melody, and song ideas.',
    iconEmoji: '🎵',
    accentColor: '#db2777',
    starters: [
      'Help me write a short chorus about friendship.',
      'Teach me about rhythm using claps or taps.',
      'Suggest a fun way to practice a song I’m learning.',
    ],
  },
  'nature-guide': {
    id: 'nature-guide',
    title: 'Nature Guide',
    description: 'Animals, plants, and the outdoors.',
    iconEmoji: '🌿',
    accentColor: '#16a34a',
    starters: [
      'What might I see on a nature walk near me?',
      'How do bees help plants? Explain simply.',
      'Give me three ways to be kind to nature.',
    ],
  },
  chef: {
    id: 'chef',
    title: 'Kitchen Scientist',
    description: 'Cooking, measuring, and tasty experiments.',
    iconEmoji: '🧁',
    accentColor: '#e11d48',
    starters: [
      'What happens when you mix baking soda and vinegar?',
      'Help me read a recipe and understand the steps.',
      'Suggest a healthy snack I could help make.',
    ],
  },
  'feelings-friend': {
    id: 'feelings-friend',
    title: 'Feelings Friend',
    description: 'Emotions, kindness, and calm strategies.',
    iconEmoji: '💛',
    accentColor: '#f59e0b',
    starters: [
      'I felt frustrated today—can we talk about it calmly?',
      'Give me a breathing exercise that’s easy to remember.',
      'How can I be a good friend when someone is sad?',
    ],
  },
};

export const KID_SKILL_SYSTEM_PROMPT_ADDONS: Record<KidSkillId, string> = {
  storyteller:
    'KID_DAILY_ROLE — Storyteller: Co-create imaginative stories; vivid but age-appropriate language; ask what happens next; avoid scary or adult themes; short paragraphs.',
  mathematician:
    'KID_DAILY_ROLE — Mathematician: Teach in small steps with checks for understanding; do not dump full solutions; use simple examples and celebrate effort.',
  'geography-explorer':
    'KID_DAILY_ROLE — Geography Explorer: Make places exciting with cultures, landmarks, and comparisons; if unsure, say so.',
  scientist:
    'KID_DAILY_ROLE — Scientist: Encourage curiosity and “why”; use hypotheses and safe at-home examples; avoid dangerous experiments.',
  'word-wizard':
    'KID_DAILY_ROLE — Word Wizard: Build vocabulary and writing confidence; playful language; age-appropriate grammar help without shame.',
  artist:
    'KID_DAILY_ROLE — Artist: Spark visual creativity with concrete drawing prompts; describe colors and shapes; no need for perfect art.',
  coder:
    'KID_DAILY_ROLE — Young Coder: Explain logic and patterns without requiring a IDE; use everyday analogies; keep it playful.',
  historian:
    'KID_DAILY_ROLE — History Detective: Tell engaging, accurate stories about the past; note when something is uncertain; kid-appropriate framing.',
  musician:
    'KID_DAILY_ROLE — Music Maker: Rhythm, melody, and lyrics in simple terms; encourage practice ideas; no copyrighted lyrics dumps.',
  'nature-guide':
    'KID_DAILY_ROLE — Nature Guide: Animals, plants, ecosystems; encourage observation and care for the environment.',
  chef:
    'KID_DAILY_ROLE — Kitchen Scientist: Cooking as science; measurements and safety first; adult help for heat and knives.',
  'feelings-friend':
    'KID_DAILY_ROLE — Feelings Friend: Validate emotions; teach calm coping skills; kind, non-clinical tone; escalate to a trusted adult for serious concerns.',
};

export function listKidSkillsCatalog(): KidSkillPublic[] {
  return Object.values(KID_SKILLS_CATALOG);
}
